// src/coverView/index.js - the staff app's "Cover check" on the rota screen
// (v132.98, docs/cover-check-plan.md Stage 2). READ-ONLY: it never writes.
//
// For the store and week on screen: people rostered against people needed,
// hour by hour, from the same maths as the manager app and the AI rota
// generator (src/worker/cover-check.js). Advice only, never a block.
//
// Free variables from index.html's inline scope (the frontend-breakup rule):
// currentStore, currentWeekStart, shifts, shifts2, ALL_STAFF, getDisplayDays,
// weekDateStr, isQatarStore, escapeHtml, showToast, switchView, DB, TARGETS,
// STORE_CLOSE_BY_DAY. Every read is guarded; a failure shows nothing rather
// than breaking the rota screen.

import {
  COVER_DAYS, openingHoursFor, timeToMinutes, neededByHour, rosteredByHour, compareDay, dayWarnings,
  weekSummary, verdictText, daySalesFor, formatHour, openHours,
  generatorContext as genCtx, needAtFn, coverWarnings as draftWarnings,
} from '../worker/cover-check.js';

let _config = null, _configAt = 0;
let _hours = null;
const _history = {};   // store|week -> [{date, target, sales}]
const CONFIG_TTL = 10 * 60 * 1000;

function esc(s) { return (typeof escapeHtml === 'function') ? escapeHtml(String(s == null ? '' : s)) : String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
function base() { return (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : ''; }

async function loadConfig() {
  if (_config && Date.now() - _configAt < CONFIG_TTL) return _config;
  try {
    const r = await fetch(base() + '/api/cover-check/config', { cache: 'no-store' });
    if (r.ok) { _config = await r.json(); _configAt = Date.now(); }
  } catch (e) { /* offline: keep the last copy */ }
  return _config;
}
async function loadHours() {
  if (_hours) return _hours;
  // v133.86 (overnight sweep, L): a failed load used to be kept as "no hours" for the whole session, so the
  // cover check used default hours until a reload. Only a real answer is kept; a failure is tried again.
  try {
    const h = (typeof DB !== 'undefined' && DB.openingHours) ? await DB.openingHours.list() : [];
    _hours = Array.isArray(h) ? h : [];
    return _hours;
  } catch (e) { return []; }
}
function dateAdd(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
async function loadHistory(store, weekDates) {
  const key = store + '|' + weekDates[0];
  if (_history[key]) return _history[key];
  try {
    const rows = (typeof DB !== 'undefined' && DB.kpi) ? await DB.kpi.list({ store, from: dateAdd(weekDates[0], -63), to: weekDates[0] }) : [];
    _history[key] = Array.isArray(rows) ? rows : (rows && Array.isArray(rows.rows) ? rows.rows : []);
  } catch (e) { _history[key] = []; }
  return _history[key];
}

// What to tell the manager when the times on screen are not the store's own.
// Each case names the action, because they are different actions: enter this
// store's hours, enter its hours for the season now active, or fill the blank
// day. Exported for tests.
export function hoursNote(source, season) {
  if (source === 'default_season') {
    const name = season === 'ramadan' ? 'Ramadan' : season === 'regular' ? 'regular' : String(season || 'current season');
    const other = season === 'ramadan' ? 'regular' : 'Ramadan';
    return 'Opening hours: the default times. This store has no ' + name + ' hours, only its ' + other
      + ' ones, so enter its ' + name + ' hours in Admin, Stores, Hours.';
  }
  if (source === 'default_day') {
    return 'Opening hours: the default times on at least one day, because that day is blank in this store\'s hours.';
  }
  if (source === 'default') {
    return 'Opening hours: the default times (this store\'s real hours are not entered yet).';
  }
  return '';
}

// Today's hardcoded rota close times, for a store without its own hours.
function fallbackHours(dayShort) {
  const closeMap = (typeof STORE_CLOSE_BY_DAY !== 'undefined' && STORE_CLOSE_BY_DAY) || {};
  const close = timeToMinutes(closeMap[dayShort] || '22:00');
  return { open: 9 * 60, close: close == null ? 22 * 60 : close };
}

// The work shifts AT this store for one day of the week: home staff with no
// other work store, plus anyone rostered in from elsewhere. Split second legs
// included. Returns [{ staffId, start, end, lunchAt, lunchMins }].
export function shiftsAtStore(shiftMap, shift2Map, staffList, store, weekStr, dayShort) {
  const homeOf = {};
  (Array.isArray(staffList) ? staffList : []).forEach((s) => { if (s && s.id != null) homeOf[String(s.id)] = s.store; });
  const out = [];
  [shiftMap || {}, shift2Map || {}].forEach((map) => {
    Object.keys(map).forEach((key) => {
      if (key.indexOf(weekStr + '_') !== 0) return;
      const rest = key.slice(weekStr.length + 1);
      const cut = rest.lastIndexOf('_');
      if (cut < 0 || rest.slice(cut + 1) !== dayShort) return;
      const sid = rest.slice(0, cut);
      const sh = map[key];
      if (!sh || sh.type !== 'work' || !sh.start || !sh.finish) return;
      const here = sh.workStore ? sh.workStore === store : homeOf[sid] === store;
      if (!here) return;
      out.push({ staffId: sid, start: sh.start, end: sh.finish, lunchAt: sh.lunchAt || null, lunchMins: sh.lunch || 0 });
    });
  });
  return out;
}

// v136.8 (boot audit): a store device reads its own store's targets for a window around today
// (targets.js); a week outside it is read here before the view uses it. Never rejects.
async function ensureTargets(store, weekStart) {
  try {
    if (typeof TARGETS !== 'undefined' && typeof TARGETS.ensureWeek === 'function') await TARGETS.ensureWeek(store, weekStart);
  } catch (e) { /* the stored targets stand, as before */ }
}

// Everything the view needs for the store and week on screen.
export async function computeWeek() {
  const store = typeof currentStore !== 'undefined' ? currentStore : null;
  const weekStart = (typeof currentWeekStart !== 'undefined') ? currentWeekStart : null;
  if (!store || !weekStart) return null;
  const cfg = await loadConfig();
  const country = (typeof isQatarStore === 'function' && isQatarStore(store)) ? 'Qatar' : 'UAE';
  if (!cfg || !cfg.configured || !cfg.configured[country]) return { store, country, configured: false };
  const rows = await loadHours();
  const days = typeof getDisplayDays === 'function' ? getDisplayDays() : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dates = days.map((_, i) => weekDateStr(weekStart, i));
  const weekStr = dates[0];
  await ensureTargets(store, weekStart);   // v136.8: a week beyond the boot window is read first
  const daily = (typeof TARGETS !== 'undefined' && TARGETS.getDaily) ? (TARGETS.getDaily() || {}) : {};
  const needHistory = dates.some((d) => !(daily[d] && Number(daily[d][store]) > 0));
  const history = needHistory ? await loadHistory(store, dates) : [];
  const shiftMap = typeof shifts !== 'undefined' ? shifts : {};
  const shift2Map = typeof shifts2 !== 'undefined' ? shifts2 : {};
  const staffList = typeof ALL_STAFF !== 'undefined' ? ALL_STAFF : [];
  let hoursSource = 'store';
  const out = dates.map((date, i) => {
    const dayIdx = new Date(date + 'T12:00:00Z').getUTCDay();
    const dayShort = days[i];
    const hours = openingHoursFor(rows, store, cfg.season, dayIdx, fallbackHours(dayShort));
    // Keep the FIRST gap named rather than collapsing every one to 'default':
    // "no hours entered" and "no Ramadan hours entered" send a manager to
    // different screens, and the second used to be reported as the first.
    if (hours.source !== 'store' && hoursSource === 'store') hoursSource = hours.source;
    const sales = daySalesFor(date, daily[date] && daily[date][store], history);
    const needed = neededByHour({ daySales: sales.value, shares: cfg.pattern[country][dayIdx], open: hours.open, close: hours.close, target: cfg.target[country] });
    const dayShifts = shiftsAtStore(shiftMap, shift2Map, staffList, store, weekStr, dayShort);
    const cmp = compareDay(needed, rosteredByHour(dayShifts, needed.map((n) => n.hour)));
    return { date, day: dayIdx, dayShort, hours, sales, needed, shifts: dayShifts, cmp, warnings: dayShifts.length ? dayWarnings(needed, cmp, dayShifts) : [] };
  });
  // Days off on the store's two busiest days (by sales), named.
  const busiest = out.slice().sort((a, b) => b.sales.value - a.sales.value).slice(0, 2).filter((d) => d.sales.value > 0);
  const offBusy = [];
  busiest.forEach((d) => {
    staffList.forEach((s) => {
      if (!s || s.store !== store) return;
      const sh = shiftMap[weekStr + '_' + s.id + '_' + d.dayShort];
      if (sh && sh.type === 'off') offBusy.push({ name: s.name, day: d.dayShort });
    });
  });
  const summary = weekSummary(out);
  return { store, country, configured: true, days: out, summary, verdict: verdictText(summary), offBusy, hoursSource, season: cfg.season, target: cfg.target[country] };
}

function cellStyle(x) {
  if (!x) return 'background:transparent';
  if (x.gap <= -0.25) return 'background:var(--rota-cover-short-bg);color:var(--warn-deep);font-weight:700';
  if (x.gap >= 0.25) return 'background:var(--rota-cover-spare-bg);color:var(--rota-info-ink)';
  return 'background:var(--rota-cover-ok-bg);color:var(--ok-deep)';
}
const fmtN = (v) => (Math.round(v * 10) / 10).toString();

// The store and week on screen, as one comparable value. computeWeek() awaits
// the network, so a slow result for last week (or the last store) can land
// after the manager has moved on: capture this before the await and drop the
// result if it no longer matches.
function viewKey() {
  const store = typeof currentStore !== 'undefined' ? currentStore : null;
  const ws = typeof currentWeekStart !== 'undefined' ? currentWeekStart : null;
  const wk = ws instanceof Date ? ws.getTime() : String(ws);
  return String(store) + '|' + wk;
}

export async function render() {
  const el = document.getElementById('coverViewBody');
  if (!el) return;
  el.innerHTML = '<div style="padding:14px;color:var(--sub);font-size:13px">Checking cover...</div>';
  const key = viewKey();
  let w = null;
  try { w = await computeWeek(); } catch (e) { w = null; }
  if (key !== viewKey()) return; // stale: a newer week or store is on screen
  if (!w) { el.innerHTML = '<div style="padding:14px;color:var(--sub);font-size:13px">Cover check is not available right now.</div>'; return; }
  if (!w.configured) {
    el.innerHTML = '<div style="padding:14px;font-size:13px"><b>Cover check is not set up yet for ' + esc(w.country) + '.</b><br><span style="color:var(--sub)">Head Office loads the sales-by-hour pattern in the manager app. Until then the rota works exactly as before.</span></div>';
    return;
  }
  const allHours = [];
  w.days.forEach((d) => openHours(d.hours.open, d.hours.close).forEach((h) => { if (allHours.indexOf(h) < 0) allHours.push(h); }));
  allHours.sort((a, b) => a - b);
  let html = '<div style="padding:10px 12px;display:grid;gap:8px">' +
    '<div style="font-size:14px;font-weight:700">' + esc(w.verdict) + '</div>' +
    '<div style="font-size:11px;color:var(--sub)">Each cell: people on the floor / people needed. <span style="' + cellStyle({ gap: -1 }) + ';padding:0 5px;border-radius:3px">short</span> <span style="' + cellStyle({ gap: 1 }) + ';padding:0 5px;border-radius:3px">spare</span> <span style="' + cellStyle({ gap: 0 }) + ';padding:0 5px;border-radius:3px">right</span>. ' +
    'Needed = the day\'s target, spread by the ' + esc(w.country) + ' sales-by-hour pattern, one person per ' + esc(w.target) + ' of sales an hour, never fewer than one. ' +
    esc(hoursNote(w.hoursSource, w.season)) + '</div>' +
    '<div style="overflow-x:auto"><table style="border-collapse:collapse;font-size:11px;min-width:100%"><thead><tr><th style="text-align:start;padding:3px 6px"></th>' +
    w.days.map((d) => '<th style="padding:3px 4px">' + esc(d.dayShort) + '<div style="font-weight:400;color:var(--sub);font-size:10px">' + (d.sales.source === 'none' ? 'no target' : Math.round(d.sales.value / 100) / 10 + 'k' + (d.sales.source === 'history' ? '*' : '')) + '</div></th>').join('') + '</tr></thead><tbody>';
  allHours.forEach((h) => {
    html += '<tr><th style="text-align:start;padding:2px 6px;font-weight:600;color:var(--sub);white-space:nowrap">' + formatHour(h) + '</th>' + w.days.map((d) => {
      const x = d.cmp.hours.find((y) => y.hour === h);
      if (!x) return '<td></td>';
      return '<td title="' + esc(COVER_DAYS[d.day] + ' ' + formatHour(h) + ': ' + fmtN(x.have) + ' on the floor, ' + x.need + ' needed') + '" style="text-align:center;padding:3px 2px;border:1px solid var(--card);' + cellStyle(x) + '">' + fmtN(x.have) + '/' + x.need + '</td>';
    }).join('') + '</tr>';
  });
  html += '<tr><th style="text-align:start;padding:3px 6px;font-size:10px;color:var(--sub)">Short / spare</th>' + w.days.map((d) =>
    '<td style="text-align:center;font-size:10px;padding:3px 2px">' + (d.shifts.length ? '<span style="color:var(--ui-warn);font-weight:700">' + fmtN(d.cmp.short) + '</span> / <span style="color:var(--rota-cover-spare-num)">' + fmtN(d.cmp.spare) + '</span>' : '<span style="color:var(--sub)">no rota</span>') + '</td>').join('') + '</tr></tbody></table></div>';
  const notes = [];
  w.days.forEach((d) => d.warnings.forEach((t) => notes.push(esc(d.dayShort) + ': ' + esc(t))));
  w.offBusy.forEach((o) => notes.push(esc(o.name) + '\'s day off is on ' + esc(o.day) + ', one of the two busiest days'));
  if (w.days.some((d) => d.sales.source === 'history')) notes.push('* no target that day: the average of the last 8 weeks\' sales on the same weekday is used');
  if (notes.length) html += '<div style="font-size:12px;display:grid;gap:3px"><b>Worth a look</b>' + notes.map((n) => '<div>- ' + n + '</div>').join('') + '</div>';
  html += '<div style="font-size:11px;color:var(--sub)">Advice only: nothing here stops you saving the rota. Nine-hour shifts overlap in the afternoon, so some spare there is normal; take breaks in those hours.</div></div>';
  el.innerHTML = html;
}

// The one-line verdict shown above every rota view. Hidden when not set up.
export async function renderVerdict() {
  const el = document.getElementById('coverVerdict');
  if (!el) return;
  const key = viewKey();
  let w = null;
  try { w = await computeWeek(); } catch (e) { w = null; }
  if (key !== viewKey()) return; // stale: a newer week or store is on screen
  if (!w || !w.configured || !w.days.some((d) => d.shifts.length)) { el.style.display = 'none'; el.innerHTML = ''; return; }
  const short = w.summary.short >= 0.5;
  el.style.display = 'block';
  el.innerHTML = '<button type="button" onclick="switchView(\'cover\')" style="all:unset;cursor:pointer;display:block;width:100%;box-sizing:border-box;padding:6px 12px;font-size:12px;font-weight:600;background:' + (short ? 'var(--rota-cover-short-bg)' : 'var(--rota-cover-ok-bg)') + ';color:' + (short ? 'var(--warn-deep)' : 'var(--ok-deep)') + '">' + esc(w.verdict) + ' <span style="font-weight:400;text-decoration:underline">See cover check</span></button>';
}

// After a shift is saved: a gentle note if that day is now short or has a
// warning. Never blocks and never throws.
export async function afterSave(dayShort) {
  try {
    const w = await computeWeek();
    if (!w || !w.configured) return;
    const d = w.days.find((x) => x.dayShort === dayShort);
    if (!d) return;
    const bits = [];
    if (d.cmp.runs.length) bits.push(d.cmp.runs.slice(0, 2).map((r) => formatHour(r.from) + ' to ' + formatHour(r.to)).join(', ') + ' short');
    d.warnings.forEach((t) => bits.push(t));
    if (bits.length && typeof showToast === 'function') showToast('Cover check, ' + dayShort + ': ' + bits.join('; '));
    renderVerdict();
  } catch (e) { /* advice only */ }
}

// ── Stage 4 (v132.100): the AI rota generator's view of the same maths ─────
// Returns null when this store's country is not set up, and the generator
// then behaves exactly as before.
export async function generatorContext() {
  const store = typeof currentStore !== 'undefined' ? currentStore : null;
  const weekStart = typeof currentWeekStart !== 'undefined' ? currentWeekStart : null;
  if (!store || !weekStart) return null;
  const cfg = await loadConfig();
  const country = (typeof isQatarStore === 'function' && isQatarStore(store)) ? 'Qatar' : 'UAE';
  if (!cfg || !cfg.configured || !cfg.configured[country]) return null;
  const rows = await loadHours();
  const days = typeof getDisplayDays === 'function' ? getDisplayDays() : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dates = days.map((_, i) => weekDateStr(weekStart, i));
  await ensureTargets(store, weekStart);   // v136.8: a week beyond the boot window is read first
  const daily = (typeof TARGETS !== 'undefined' && TARGETS.getDaily) ? (TARGETS.getDaily() || {}) : {};
  const targetsByDate = {};
  dates.forEach((d) => { targetsByDate[d] = daily[d] ? daily[d][store] : null; });
  const history = dates.some((d) => !(Number(targetsByDate[d]) > 0)) ? await loadHistory(store, dates) : [];
  return genCtx({ store, country, dates, config: cfg, hoursRows: rows, targetsByDate, history });
}
export function needAt(gctx, day) { return needAtFn(gctx, day); }
export function coverWarnings(workShifts, gctx) { return draftWarnings(workShifts, gctx); }
